import { Injectable } from '@angular/core';
import {
  INVALID_CITY_ERROR,
  PACKAGE_TYPE
} from '../utils/booking-widget.constants';
import {
  CitiesDetails,
  FilterOriginDestDetails,
  SearchCitiesDetails
} from '../utils/origin-dest-cities.interface';

@Injectable({
  providedIn: 'root'
})
export class FilterCitiesUtilService {
  filterOriginDestCitiesList(
    originDestData: FilterOriginDestDetails
  ): SearchCitiesDetails {
    let errorCode!: INVALID_CITY_ERROR;
    const searchCitiesDetails: SearchCitiesDetails = { cities: [] };
    if (originDestData.isOrigin) {
      searchCitiesDetails.cities = originDestData.originCityList.filter(
        (city: CitiesDetails) => {
          return this._matchCriteria(city, originDestData.searchQuery, true);
        }
      );
      errorCode = INVALID_CITY_ERROR.invalidOriginErrMsg;
    } else {
      /** Filter Destinations by User Query */
      searchCitiesDetails.cities = originDestData.destCityList.filter(
        (city: CitiesDetails) => {
          return this._matchCriteria(city, originDestData.searchQuery, false);
        }
      );
      if (!searchCitiesDetails.cities.length) {
        return {
          ...searchCitiesDetails,
          errorCode: INVALID_CITY_ERROR.invalidDestErrMsg
        };
      }
      /** Filter Destinations by Package Type */
      const searchPkgType = this._getPackageType(originDestData.pkgType);
      searchCitiesDetails.cities = searchCitiesDetails.cities.filter(
        (city: CitiesDetails) => {
          return city.pkgType.toString().toLowerCase().includes(searchPkgType);
        }
      );
      errorCode = INVALID_CITY_ERROR.invalidDestPkgErrMsg;
    }
    if (!searchCitiesDetails.cities.length) {
      return { ...searchCitiesDetails, errorCode };
    }
    this._orderCityList(searchCitiesDetails, originDestData.searchQuery);
    return searchCitiesDetails;
  }

  private _getPackageType(packageType: string): PACKAGE_TYPE {
    return packageType === PACKAGE_TYPE.HOTEL_CAR
      ? PACKAGE_TYPE.HOTEL_GROUND
      : <PACKAGE_TYPE>packageType.toLowerCase();
  }

  private _matchCriteria(
    airport: CitiesDetails,
    query: string,
    isOrigin: boolean
  ): boolean {
    const criteria = isOrigin
      ? [
          airport.code,
          airport.cityCode,
          airport.cityName,
          airport.cityServe,
          airport.stateName,
          airport.airportName,
          airport.countryName
        ]
      : [
          airport.code,
          airport.cityCode,
          airport.cityName,
          airport.cityServe,
          airport.stateName,
          airport.countryName,
          airport.airportName,
          airport.airportCode
        ];
    return (
      criteria.findIndex(
        (key: string | undefined) =>
          key?.toLowerCase().includes(query.toLowerCase()) &&
          (isOrigin || airport.internalBkgOnly !== true)
      ) !== -1
    );
  }

  private _orderCityList(
    citiesDetails: SearchCitiesDetails,
    query: string
  ): void {
    const citiesWithPriority = citiesDetails.cities.map((city) => ({
      city,
      priority: this._getPriority(city, query)
    }));
    citiesWithPriority.sort((a, b) => a.priority - b.priority);
    citiesDetails.cities = citiesWithPriority.map((item) => item.city);
  }

  /** Priority calculation for re-ordering the results */
  private _getPriority(city: CitiesDetails, query: string): number {
    const upperQuery = query.toUpperCase();
    if (city.airportCode === query || city.cityCode === query) return 1;
    if (city.airportName?.toUpperCase().startsWith(upperQuery)) return 2;
    return this._checkCityName(city, upperQuery);
  }

  private _checkCityName(city: CitiesDetails, upperQuery: string): number {
    if (city.cityName?.toUpperCase().startsWith(upperQuery)) return 3;
    if (city.cityServe?.toUpperCase().includes(upperQuery)) return 4;
    return this._checkLocation(city, upperQuery);
  }

  private _checkLocation(city: CitiesDetails, upperQuery: string): number {
    if (
      city.stateName?.toUpperCase().startsWith(upperQuery) ||
      city.countryName?.toUpperCase().startsWith(upperQuery)
    )
      return 5;
    /** Returns default value if no match */
    return 99;
  }
}
